import React, { useMemo, useState } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PaymentResult = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [continueError, setContinueError] = useState('');

    const getStoredGuestConversationId = () =>
        sessionStorage.getItem('guestConversationId') ||
        localStorage.getItem('guestConversationId');

    const getStoredIsGuest = () => {
        const storedGuestUserRaw =
            sessionStorage.getItem('guestUser') ||
            localStorage.getItem('guestUser');
        try {
            return !!(storedGuestUserRaw && JSON.parse(storedGuestUserRaw));
        } catch (e) {
            return false;
        }
    };

    const data = useMemo(() => {
        const statusParam = (searchParams.get('status') || '').toLowerCase();
        const isSuccess = statusParam === 'success';
        const isFailure = statusParam === 'failed';

        const storedGuestConversationId = getStoredGuestConversationId();
        const isGuest = getStoredIsGuest();

        return {
            statusParam,
            isSuccess,
            isFailure,
            storedGuestConversationId,
            isGuest,
        };
    }, [searchParams]);

    const handleContinueChat = () => {
        const conversationId = getStoredGuestConversationId();
        const isGuest = getStoredIsGuest();

        if (!conversationId) {
            setContinueError(
                'Unable to continue chat: conversation not found for this session. Please reopen the chat from the QR link.'
            );
            return;
        }
        setContinueError('');
        navigate('/active-chat', {
            state: {
                conversationId,
                isGuest,
            },
        });
    };

    const title = data.isSuccess
        ? 'Payment Successful'
        : data.isFailure
        ? 'Payment Failed'
        : 'Payment Status';

    const bodyText = data.isSuccess
        ? 'Your payment has been confirmed.'
        : data.isFailure
        ? 'Your payment could not be completed.'
        : 'We could not determine the payment status.';

    return (
        <Container
            maxWidth={false}
            sx={{
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
            }}>
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 520,
                    textAlign: 'center',
                    px: 2,
                }}>
                <Box
                    sx={{
                        width: 88,
                        height: 88,
                        mx: 'auto',
                        mb: 3,
                        borderRadius: '50%',
                        backgroundColor: '#113524',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    {data.isFailure ? (
                        <CloseIcon sx={{ color: '#fff', fontSize: 46 }} />
                    ) : (
                        <CheckIcon sx={{ color: '#fff', fontSize: 46 }} />
                    )}
                </Box>

                <Typography
                    variant='h5'
                    sx={{ mb: 1, fontWeight: 600, color: '#111827' }}>
                    {title}
                </Typography>
                <Typography
                    variant='body1'
                    sx={{
                        color: '#4B5563',
                        maxWidth: 420,
                        mx: 'auto',
                        lineHeight: 1.6,
                        mb: 3,
                    }}>
                    {bodyText}
                </Typography>

                {!!continueError && (
                    <Typography
                        variant='body2'
                        sx={{
                            color: '#DC2626',
                            maxWidth: 420,
                            mx: 'auto',
                            lineHeight: 1.6,
                            mb: 2,
                        }}>
                        {continueError}
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                    }}>
                    <Button
                        variant='contained'
                        onClick={handleContinueChat}
                        sx={{
                            backgroundColor: '#113524',
                            '&:hover': { backgroundColor: '#0a2318' },
                        }}>
                        Continue Chat
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default PaymentResult;
